import { useParams, useSearchParams } from "react-router-dom"
import PartnerService from '../../../shared/services/partner/partner.service'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { IOrganisationDetails } from '../../../shared/services/organisation/organisation.model'
import { IPartnerClientReadiness, IPartnerClientTask } from '../../../shared/services/partner/partner.model'
import { IReadinessFinding } from '../../../shared/services/readiness/readiness.model'
import { PageOuterContainer } from '../../../root-components/layouts/page-outer-container'
import { ShallowFormDisplay } from '../../presentation-components/form-field-components/general/shallow-form-display'
import { Alert, Box, Button, Chip, Grid, MenuItem, Stack, Tab, Tabs, TextField, Typography } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import moment from 'moment'
import { PageTitle } from '../../presentation-components/display-elements-components/page-title'
import { BackButtonLink } from '../../presentation-components/display-elements-components/back-button-link'
import { LoadingPage } from '../loading-page'
import { LoadingSpinner } from '../../presentation-components/display-elements-components/loading-spinner'
import { InformationBox } from '../../presentation-components/miscellaneous-components/information-box'
import { AnalyticMetric } from '../../presentation-components/analytics-components/graphs/analytic-metric'
import { ReadinessStateChip } from '../../presentation-components/readiness-components/readiness-state-chip'
import { ReadinessChecksList } from '../../presentation-components/readiness-components/readiness-checks-list'
import { CreateTaskFromFindingDialog } from '../../presentation-components/readiness-components/create-task-from-finding-dialog'
import { ActionStatusChip } from '../../presentation-components/emissons-components/overview/emissions-actions/action-status-chip'
import { TableDisplay } from '../../presentation-components/miscellaneous-components/table-display'
import { IHeadCell } from '../../../shared/interfaces/table-display.model'
import { IconName } from '../../../shared/interfaces/icon-picker.model'
import { LoginOutlined } from '@mui/icons-material'
import { useSwitchClient } from '../../../hooks/use-switch-client'
import { UserDetailsContext } from '../../../context/user-details/user-details'
import { actionEntityName, friendlyActionTitle } from '../../../shared/services/report-action/report-action-title'

// Ink chip styling: the readiness palette's "work to do" colour, where red would read as a
// failure.
const INK_CHIP_SX = { borderColor: 'text.primary', color: 'text.primary' }

const periodLabel = (period: { friendlyName: string | null, startDate: string, endDate: string }) =>
  period.friendlyName || `${moment(period.startDate).format('DD/MM/YYYY')} to ${moment(period.endDate).format('DD/MM/YYYY')}`

// The partner's read-only view of one client: the client's own Report Readiness for a period
// (the same checks, vocabulary and palette the client sees), its open tasks, and, for partner
// admins and analysts, the option to create a task in the client's account from a finding. The
// deep links on findings are relative to the client's account, so nothing here follows them;
// fixing is done by opening the account.
export const PartnerClientPage = () => {
  const { id } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  //Services
  const partnerService = useMemo(() => new PartnerService(), [])
  //Contexts
  const userDetailsContext = useContext(UserDetailsContext)
  //Switching
  const { switchToClient, isSwitching } = useSwitchClient()
  //Client
  const [client, setClient] = useState<IOrganisationDetails>()
  const [drillIn, setDrillIn] = useState<IPartnerClientReadiness | null>(null)
  const [noPeriods, setNoPeriods] = useState(false)
  const [createTaskTarget, setCreateTaskTarget] = useState<IReadinessFinding | null>(null)
  //Loading State
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [loadFailed, setLoadFailed] = useState<boolean>(false)
  const [readinessLoading, setReadinessLoading] = useState<boolean>(true)
  const [readinessFailed, setReadinessFailed] = useState<boolean>(false)
  //Table State
  const [selected, setSelected] = useState<string[]>([])
  const [perPage, setPerPage] = useState(10)

  const requestedPeriodId = searchParams.get('reportPeriodId')
  const tab = searchParams.get('tabId') === '1' ? 1 : 0
  const roles = userDetailsContext.userDetails?.roles ?? []
  const canCreateTask = roles.includes('admin') || roles.includes('analyst')

  useEffect(() => {
    getClient()
  }, [id])

  const getClient = () => {
    if (!id) return
    setIsLoading(true)
    setLoadFailed(false)
    partnerService.getOne(id)
      .then(response => {
        setClient(response)
        setIsLoading(false)
      })
      .catch(err => {
        console.log(err)
        setLoadFailed(true)
        setIsLoading(false)
      })
  }

  //One request per view: it is written to the client's audit trail server-side, so the page
  //fetches once and shares the result between the header, tiles, checks and tasks.
  const loadReadiness = useCallback(() => {
    if (!id) return
    setReadinessLoading(true)
    setReadinessFailed(false)
    partnerService.getClientReadiness(id, requestedPeriodId)
      .then(response => {
        setDrillIn(response)
        setNoPeriods(false)
        setReadinessLoading(false)
      })
      .catch(err => {
        console.log(err)
        setDrillIn(null)
        //404: the client has no reporting periods yet (or the requested period is not theirs).
        setNoPeriods(err?.response?.status === 404)
        setReadinessFailed(err?.response?.status !== 404)
        setReadinessLoading(false)
      })
  }, [id, requestedPeriodId, partnerService])

  useEffect(() => {
    loadReadiness()
  }, [loadReadiness])

  const setParam = (key: string, value: string | null) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      if (value) next.set(key, value)
      else next.delete(key)
      return next
    }, { replace: true })
  }

  const readiness = drillIn?.readiness ?? null
  const score = readiness?.score
  const blockingCount = score ? score.integrity.blocks_reporting : 0
  const openCount = readiness ? readiness.findings.length : 0

  //Tasks table: read-only, the client's open tasks on the evaluated period, with the overdue
  //marker computed server-side.
  const taskHeadCells: IHeadCell[] = [
    { id: 'title', numeric: false, label: 'Task', sortable: true, isHeading: true },
    { id: 'entity', numeric: false, label: 'Entity', sortable: true },
    { id: 'owner', numeric: false, label: 'Owner', sortable: true },
    { id: 'dueDate', numeric: false, label: 'Due date', sortable: true, sortValue: 'dueDateValue' },
    { id: 'status', numeric: false, label: 'Status', sortable: false },
  ]
  const taskTitle = (task: IPartnerClientTask) => friendlyActionTitle({ title: task.title, actionCode: task.actionCode, location: task.location as any, vehicle: task.vehicle as any })
  const taskRows = (drillIn?.tasks ?? []).map(task => ({
    id: task.id,
    title: taskTitle(task),
    entity: actionEntityName({ location: task.location as any, vehicle: task.vehicle as any }) || '—',
    owner: task.user ? `${task.user.firstName} ${task.user.surname}` : 'Unassigned',
    dueDate: task.endDate ? moment(task.endDate).format('D MMM YYYY') : '—',
    dueDateValue: task.endDate ? moment(task.endDate).valueOf() : Number.MAX_SAFE_INTEGER,
    status: (
      <Stack direction='row' spacing={0.5} useFlexGap flexWrap='wrap'>
        {task.overdue && <Chip size='small' variant='outlined' label='Overdue' sx={INK_CHIP_SX} />}
        {task.status && <ActionStatusChip status={task.status} reason={task.statusReason} />}
        {!task.overdue && !task.status && <Typography variant='body2' sx={{ color: 'text.secondary' }}>Open</Typography>}
      </Stack>
    ),
  }))

  return (
    <PageOuterContainer>
      <BackButtonLink buttonText='Back to Clients' link='/partner-clients' />
      <LoadingPage pageReady={!isLoading}>
        {loadFailed &&
          <Stack spacing={2} sx={{ mt: 2, alignItems: 'flex-start' }}>
            <Alert severity='error' sx={{ width: '100%' }}>
              There was an error loading this client, please try again.
            </Alert>
            <Button variant='outlined' onClick={getClient}>
              Try again
            </Button>
          </Stack>}
        {!loadFailed && client &&
          <>
            <Stack sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
              <Stack sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-start', gap: 1.5 }}>
                {client.logoFilepath && <img src={client.logoFilepath} alt='client logo' style={{ width: '100px' }} />}
                <PageTitle title={client.name} />
                {readiness && <ReadinessStateChip state={readiness.state} />}
              </Stack>
              <LoadingButton variant='text' loading={isSwitching} onClick={() => switchToClient(client.id!)}>
                <LoginOutlined />
                <Typography sx={{ ml: 0.5 }}>Access Client Account</Typography>
              </LoadingButton>
            </Stack>
            {drillIn &&
              <Stack direction='row' alignItems='center' spacing={2} sx={{ mt: 1 }} useFlexGap flexWrap='wrap'>
                <Typography variant='body1' sx={{ color: 'text.secondary' }}>
                  Readiness for {periodLabel(drillIn.reportPeriod)}
                  {drillIn.reportPeriod.friendlyName ? ` (${moment(drillIn.reportPeriod.startDate).format('D MMM YYYY')} to ${moment(drillIn.reportPeriod.endDate).format('D MMM YYYY')})` : ''}
                  {drillIn.reportPeriod.locked ? ', locked' : ''}
                  {drillIn.reportPeriod.reportingDeadline ? `, reporting deadline ${moment(drillIn.reportPeriod.reportingDeadline).format('D MMM YYYY')}` : ', no reporting deadline set'}.
                  The client may have a different period selected on their own Readiness page.
                </Typography>
                {drillIn.reportPeriods.length > 1 &&
                  <TextField
                    select
                    size='small'
                    label='Reporting period'
                    value={drillIn.reportPeriod.id}
                    onChange={(e) => setParam('reportPeriodId', e.target.value)}
                    sx={{ minWidth: 220 }}
                  >
                    {drillIn.reportPeriods.map(period => (
                      <MenuItem key={period.id} value={period.id}>{periodLabel(period)}{period.locked ? ' (locked)' : ''}</MenuItem>
                    ))}
                  </TextField>}
              </Stack>}

            <Stack sx={{ mt: 3 }}>
              <ShallowFormDisplay name='Subscription Status' value={client.subscriptionActive
                ? <Chip label='Active' variant='outlined' color='success' size='small' />
                : <Chip label='Not active' variant='outlined' size='small' sx={INK_CHIP_SX} />}
                isTopRow={true}
              />
              <ShallowFormDisplay name='Account Created' value={moment(client.createdAt).format('DD/MM/YYYY')} isBottomRow={client.subscriptionActive ? true : false} />
              {!client.subscriptionActive &&
                <>
                  <ShallowFormDisplay name='Trial Status' value={client.freeTrialDaysRemaining !== 0
                    ? <Chip label='Active' variant='outlined' color='success' size='small' />
                    : <Chip label='Not active' variant='outlined' size='small' sx={INK_CHIP_SX} />}
                  />
                  <ShallowFormDisplay name='Trial Days Granted' value={client.freeTrialDaysGranted} />
                  <ShallowFormDisplay name='Trial Days Remaining' value={client.freeTrialDaysRemaining} isBottomRow={true} />
                </>}
            </Stack>

            {readinessLoading && <Box sx={{ mt: 6 }}><LoadingSpinner /></Box>}

            {!readinessLoading && noPeriods &&
              <InformationBox
                icon={IconName.CheckCircle}
                text='This client has no reporting periods yet.'
                additionalText='Readiness is evaluated per reporting period. Open the client account to create one.'
                marginTop={3}
              />}

            {!readinessLoading && readinessFailed &&
              <Stack spacing={2} sx={{ mt: 3, alignItems: 'flex-start' }}>
                <Alert severity='error' sx={{ width: '100%' }}>Readiness could not be evaluated for this client. Please try again.</Alert>
                <Button variant='outlined' onClick={loadReadiness}>Try again</Button>
              </Stack>}

            {!readinessLoading && drillIn && readiness && score && <>
              <Grid container spacing={2} sx={{ mt: 1, mb: 1 }}>
                <Grid item xl={4} lg={4} md={4} sm={12} xs={12}>
                  <AnalyticMetric
                    fitContent
                    title='Coverage'
                    tooltip="Expected data comes from the client's own set-up: emission sources switched on, plus sites configured for monthly bills. Coverage counts how much of that expected data is present for this period."
                    value={score.coverage.taggedGroupsWithData}
                    units={`of ${score.coverage.taggedGroupsTotal} sources with data`}
                    footer={score.coverage.locationMonthsExpected > 0
                      ? <Typography variant='caption' sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>
                        Monthly readings: {score.coverage.locationMonthsWithData} of {score.coverage.locationMonthsExpected} expected so far
                      </Typography>
                      : undefined}
                  />
                </Grid>
                <Grid item xl={4} lg={4} md={4} sm={12} xs={12}>
                  <AnalyticMetric
                    fitContent
                    title='Data quality'
                    tooltip='The share of the reported total calculated from actual usage rather than money spent, weighted by emissions. Higher is better: spend-based figures are estimates.'
                    value={score.quality.spendBasedSharePct !== null ? 100 - score.quality.spendBasedSharePct : 0}
                    units='% activity-based'
                    unavailable={score.quality.spendBasedSharePct === null}
                    unavailableMessage='No emissions reported for this period yet.'
                    footer={<Typography variant='caption' sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>
                      of {score.quality.totalTco2e.toLocaleString('en-GB', { maximumFractionDigits: 2 })} tCO2e reported
                    </Typography>}
                  />
                </Grid>
                <Grid item xl={4} lg={4} md={4} sm={12} xs={12}>
                  <AnalyticMetric
                    fitContent
                    title='Critical findings'
                    tooltip='Findings that make the report wrong or incomplete. Any critical finding keeps the period at Not ready.'
                    value={blockingCount}
                    units={blockingCount === 1 ? 'critical finding' : 'critical findings'}
                    footer={<Typography variant='caption' sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>
                      {blockingCount > 0 ? 'The client fixes these before generating the report.' : 'Nothing is blocking this report.'}
                    </Typography>}
                  />
                </Grid>
              </Grid>

              <Box sx={{ borderBottom: 1, borderColor: 'lightGrey.main', mt: 2 }}>
                <Tabs value={tab} onChange={(_, next: number) => setParam('tabId', next ? String(next) : null)}>
                  <Tab label={
                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
                      <Typography>Checks</Typography>
                      {openCount > 0 && <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: 'primary.main', color: 'common.white', width: '25px', height: '25px', borderRadius: '100%' }}>
                        {openCount}
                      </Box>}
                    </Box>} sx={{ textTransform: 'capitalize', borderRadius: 1 }} />
                  <Tab label={
                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
                      <Typography>Tasks</Typography>
                      {drillIn.tasks.length > 0 && <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: 'primary.main', color: 'common.white', width: '25px', height: '25px', borderRadius: '100%' }}>
                        {drillIn.tasks.length}
                      </Box>}
                    </Box>} sx={{ textTransform: 'capitalize', borderRadius: 1 }} />
                </Tabs>
              </Box>

              {tab === 0 && <Box sx={{ mt: 2 }}>
                {readiness.reportPeriodLocked &&
                  <Alert severity='info' sx={{ mb: 2 }}>This reporting period is locked. Findings are shown for reference; tasks cannot be created on it.</Alert>}
                {readiness.findings.length + readiness.acknowledged.length === 0
                  ? <InformationBox
                    icon={IconName.CheckCircle}
                    text='No open findings for this period.'
                    additionalText="Checks run continuously against the client's data, so new findings appear here on their own as it changes."
                    marginTop={2}
                  />
                  : <ReadinessChecksList
                    findings={readiness.findings}
                    acknowledged={readiness.acknowledged}
                    view='all'
                    readOnly
                    canAcknowledge={false}
                    canCreateTask={canCreateTask && !readiness.reportPeriodLocked}
                    onAcknowledge={() => undefined}
                    onRelease={() => undefined}
                    onCreateTask={(finding) => setCreateTaskTarget(finding)}
                  />}
              </Box>}

              {tab === 1 && <Box sx={{ mt: 2 }}>
                {drillIn.tasks.length === 0
                  ? <InformationBox icon={IconName.CheckCircle} text='There are no open tasks for this reporting period.' marginTop={1} />
                  : <TableDisplay
                    headCells={taskHeadCells}
                    rows={taskRows}
                    selected={selected}
                    setSelected={setSelected}
                    totalRecords={taskRows.length}
                    perPage={perPage}
                    setPerPage={setPerPage}
                    defaultOrderBy='dueDate'
                    defaultOrder='asc' />}
              </Box>}

              <CreateTaskFromFindingDialog
                open={!!createTaskTarget}
                finding={createTaskTarget}
                reportPeriodId={drillIn.reportPeriod.id}
                users={drillIn.assignableUsers}
                submit={(payload) => partnerService.createClientTaskFromFinding(client.id!, drillIn.reportPeriod.id, payload)}
                successMessage={`Task created in ${client.name}`}
                closeHandler={() => setCreateTaskTarget(null)}
                onCreated={loadReadiness}
              />
            </>}
          </>}
      </LoadingPage>
    </PageOuterContainer>
  )
}
